import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import { Layout, Menu, Badge, Button, Tooltip } from 'antd'
import type { MenuProps } from 'antd'
import { HeartIcon } from 'lucide-react'
import React, { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/features/auth/hooks/useAuth'
import { TRANSITIONS } from '@/shared/constants/transitions'
import { useClickOutside } from '@/shared/hooks/useClickOutside'
import { useThemeColors } from '@/shared/hooks/useThemeColors'
import { SidebarProps } from '@/types/layout'
import { Z_INDEX } from '@/types/layout'



const { Sider } = Layout
const PADDING_SIZE = 24

export const Sidebar: React.FC<SidebarProps> = ({
  navigationItems,
  currentPath,
  collapsed,
  mobileMenuOpen,
  onToggleCollapse,
  onCloseMobileMenu,
}) => {
  const sidebarRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const [isMobile, setIsMobile] = useState(false)
  const colors = useThemeColors()
  const { logout } = useAuth()

  // State for managing open submenus
  const [openSubmenuKeys, setOpenSubmenuKeys] = useState<string[]>([])

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Close mobile menu when clicking outside
  useClickOutside(sidebarRef, () => {
    if (mobileMenuOpen && isMobile) {
      onCloseMobileMenu()
    }
  })

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'settings') {
      navigate('/settings')
    } else if (key === 'logout') {
      logout()
    } else if (key === 'help') {
      // TODO: Implement help logic (open help modal, navigate to help page, etc.)
      console.log('Help clicked')
    } else if (key) {
      // Only navigate if key is not empty (empty path means parent with children)
      navigate(key)
    }

    // Close mobile menu on navigation
    if (mobileMenuOpen && isMobile) {
      onCloseMobileMenu()
    }
  }

  // Convert navigation items to Ant Design Menu items
  const menuItems: MenuProps['items'] = navigationItems.map((item) => {
    const Icon = item.icon

    // If item has children, create a submenu
    if (item.children && item.children.length > 0) {
      return {
        key: item.id || item.path,
        icon: Icon && <Icon size={20} />,
        label: item.label,
        children: item.children.map((child) => {
          const ChildIcon = child.icon
          return {
            key: child.path,
            icon: ChildIcon && <ChildIcon size={18} />,
            label:
              child.badge && child.badge > 0 ? (
                <div className="flex items-center justify-between w-full">
                  <span>{child.label}</span>
                  <Badge count={child.badge} size="small" />
                </div>
              ) : (
                child.label
              ),
          }
        }),
      }
    }

    // Regular menu item without children
    return {
      key: item.path,
      icon: Icon && <Icon size={20} />,
      label:
        item.badge && item.badge > 0 ? (
          <div className="flex items-center justify-between w-full">
            <span>{item.label}</span>
            <Badge count={item.badge} size="small" />
          </div>
        ) : (
          item.label
        ),
    }
  })

  // Get selected key and determine which submenus should be open
  // IMPORTANT: Check longer (more specific) paths FIRST to avoid false matches
  // e.g., /sos/first-aid should match before /sos
  let selectedKey = currentPath
  const initialOpenKeys: string[] = []
  let foundMatch = false

  // First pass: Check all children, sorted by path length (longest first)
  for (const item of navigationItems) {
    if (item.children && !foundMatch) {
      // Sort children by path length descending so more specific paths are checked first
      const sortedChildren = [...item.children].sort(
        (a, b) => (b.path?.length || 0) - (a.path?.length || 0)
      )

      for (const child of sortedChildren) {
        if (!child.path) continue

        // Check exact match first
        if (currentPath === child.path) {
          selectedKey = child.path
          initialOpenKeys.push(item.id || item.path)
          foundMatch = true
          break
        }
        // Then check prefix match
        if (currentPath.startsWith(child.path + '/')) {
          selectedKey = child.path
          initialOpenKeys.push(item.id || item.path)
          foundMatch = true
          break
        }
      }
    }
  }

  // Second pass: If no child matched, check top-level items (without children)
  if (!foundMatch) {
    for (const item of navigationItems) {
      if (
        item.path &&
        !item.children &&
        (currentPath === item.path || currentPath.startsWith(item.path + '/'))
      ) {
        selectedKey = item.path
        break
      }
    }
  }

  // Initialize openSubmenuKeys based on current path
  useEffect(() => {
    if (initialOpenKeys.length > 0) {
      setOpenSubmenuKeys(initialOpenKeys)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath])

  // Handle submenu open/close
  const handleOpenChange = (keys: string[]) => {
    setOpenSubmenuKeys(keys)
  }

  return (
    <>
      {/* Backdrop for mobile */}
      {mobileMenuOpen && isMobile && (
        <div
          className="fixed inset-0 backdrop-blur-sm"
          style={{
            zIndex: Z_INDEX.MOBILE_BACKDROP,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            animation: 'fadeIn 0.3s ease-out',
          }}
          aria-hidden="true"
          onClick={onCloseMobileMenu}
        />
      )}

      {/* Sidebar Container with Floating Effect */}
      <div
        className={isMobile ? 'fixed top-0 left-0 h-screen' : 'sticky top-0'}
        style={{
          zIndex: Z_INDEX.SIDEBAR,
          padding: isMobile
            ? '0'
            : `${PADDING_SIZE}px 0 ${PADDING_SIZE}px ${PADDING_SIZE}px`,
          height: isMobile ? '100vh' : 'calc(100vh)',
        }}
      >
        {/* Sidebar using Ant Design Sider */}
        <Sider
          ref={sidebarRef}
          collapsed={isMobile ? !mobileMenuOpen : collapsed}
          collapsedWidth={isMobile ? 0 : 80}
          width={320}
          breakpoint="md"
          trigger={null}
          style={{
            backgroundColor: colors.isDark ? '#0f2929' : '#FFFFFF',
            border: 'none',
            overflow: 'hidden',
            height: isMobile ? '100vh' : `calc(100vh - ${PADDING_SIZE * 2}px)`,
            borderRadius: isMobile ? '0' : '16px',
            boxShadow: isMobile
              ? 'none'
              : colors.isDark
                ? '0 4px 24px rgba(0, 0, 0, 0.4)'
                : '0 4px 24px rgba(0, 0, 0, 0.08)',
            transition: `${TRANSITIONS.THEME}, transform 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)`,
            transform:
              isMobile && !mobileMenuOpen
                ? 'translateX(-100%)'
                : 'translateX(0)',
          }}
        >
          {/* Logo Section with Collapse Button */}
          <div className="flex items-center justify-between p-4">
            {isMobile || !collapsed ? (
              <>
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                    style={{
                      background:
                        'linear-gradient(135deg, #00A19B 0%, #04BFAD 100%)',
                      boxShadow: '0 4px 12px rgba(0, 161, 155, 0.3)',
                    }}
                  >
                    <HeartIcon size={20} />
                  </div>
                  <span
                    className="font-bold text-lg whitespace-nowrap"
                    style={{
                      color: colors.textPrimary,
                      transition: TRANSITIONS.COLOR,
                    }}
                  >
                    Kolia
                  </span>
                </div>
                {!isMobile && (
                  <Tooltip title="Thu gọn thanh bên" placement="bottom">
                    <Button
                      type="text"
                      icon={<MenuFoldOutlined style={{ fontSize: 18 }} />}
                      onClick={onToggleCollapse}
                      style={{
                        color: colors.textSecondary,
                        transition: TRANSITIONS.FAST,
                      }}
                      onMouseEnter={(e) => {
                        const target = e.currentTarget as HTMLElement
                        target.style.color = colors.primaryHover
                        target.style.backgroundColor = colors.backgroundHover
                      }}
                      onMouseLeave={(e) => {
                        const target = e.currentTarget as HTMLElement
                        target.style.color = colors.textSecondary
                        target.style.backgroundColor = 'transparent'
                      }}
                    />
                  </Tooltip>
                )}
              </>
            ) : (
              <div className="w-full flex flex-col items-center gap-2">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                  style={{
                    background:
                      'linear-gradient(135deg, #00A19B 0%, #04BFAD 100%)',
                    boxShadow: '0 4px 12px rgba(0, 161, 155, 0.3)',
                  }}
                >
                  <HeartIcon size={20} />
                </div>
                <Tooltip title="Mở rộng thanh bên" placement="right">
                  <Button
                    type="text"
                    icon={<MenuUnfoldOutlined style={{ fontSize: 18 }} />}
                    onClick={onToggleCollapse}
                    style={{
                      color: colors.textSecondary,
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      const target = e.currentTarget as HTMLElement
                      target.style.color = colors.primaryHover
                      target.style.backgroundColor = colors.backgroundHover
                    }}
                    onMouseLeave={(e) => {
                      const target = e.currentTarget as HTMLElement
                      target.style.color = colors.textSecondary
                      target.style.backgroundColor = 'transparent'
                    }}
                  />
                </Tooltip>
              </div>
            )}
          </div>

          {/* Navigation Menu - Scrollable */}
          <div
            className="flex-1 overflow-y-auto py-4 custom-scrollbar"
            style={{
              // height: isMobile
              //   ? 'calc(100vh - 64px - 96px)'
              //   : `calc(100vh - ${PADDING_SIZE * 2}px - 64px - 96px)`,
              padding: collapsed ? '0' : '0 12px',
            }}
          >
            <Menu
              mode="inline"
              selectedKeys={[selectedKey]}
              openKeys={openSubmenuKeys}
              onOpenChange={handleOpenChange}
              onClick={handleMenuClick}
              items={menuItems}
              inlineCollapsed={collapsed}
              style={{
                height: '100%',
                backgroundColor: 'transparent',
                border: 'none',
                color: colors.textPrimary,
                transition: TRANSITIONS.COLOR,
                fontWeight: 500,
              }}
              theme={colors.isDark ? 'dark' : 'light'}
            />
          </div>

          {/* Bottom Menu - Help */}
          <div
            style={{
              backgroundColor: 'transparent',
              padding: collapsed ? '12px 0' : '12px',
              transition: TRANSITIONS.BACKGROUND,
            }}
          >
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 20 }} />}
              onClick={() => handleMenuClick({ key: 'help' } as any)}
              block
              style={{
                height: collapsed ? '48px' : 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: collapsed ? '0' : '12px',
                padding: collapsed ? '0' : '12px 16px',
                color: colors.textSecondary,
                fontSize: '14px',
                fontWeight: 500,
                transition: TRANSITIONS.FAST,
                borderRadius: '8px',
              }}
              onMouseEnter={(e) => {
                const target = e.currentTarget as HTMLElement
                target.style.color = colors.primary
                target.style.backgroundColor = colors.backgroundHover
              }}
              onMouseLeave={(e) => {
                const target = e.currentTarget as HTMLElement
                target.style.color = colors.textSecondary
                target.style.backgroundColor = 'transparent'
              }}
            >
              {!collapsed && <span>Trợ giúp</span>}
            </Button>
          </div>
        </Sider>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: ${colors.scrollThumb};
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: ${colors.scrollThumbHover};
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </>
  )
}

export default Sidebar
